import React, { useState } from 'react';
import { Grid, GridColumn, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { useGetAssignmentsRules, useGlobalizationContext, useTranslation } from '@cs-cms/state-management';
import { PermissionViewModel, mapToViewModel } from '@cs-cms/types';
import { PermissionRoleExplanation, useUIConfig } from '../../UiProvider';

const renderParagraphs = (lines: string[]): React.ReactNode => {
  return lines.map((line, index) => <p key={index}>{line}</p>);
};

const RoleExplanationTable: React.FC<{ rows: PermissionRoleExplanation[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return null;
  }

  const showApprovers = rows.some(row => (row.approvers?.length ?? 0) > 0);

  return (
    <table className="roleExplanationTable">
      <thead>
        <tr>
          <th scope="col">Role</th>
          <th scope="col">Permissions</th>
          {showApprovers && <th scope="col">Role approver</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.role}>
            <td>{row.role}</td>
            <td>{renderParagraphs(row.permissions)}</td>
            {showApprovers && <td>{renderParagraphs(row.approvers ?? [])}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const PermissionsManagement: React.FC = () => {
  const { data: assignmentRules, isLoading, isError } = useGetAssignmentsRules();
  const { permissionRoleExplanations } = useUIConfig();
  const [searchText, setSearchText] = useState<string>('');
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(10);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const getFilteredData = (): PermissionViewModel[] => {
    if (!assignmentRules) {
      return [];
    }

    const viewModels = assignmentRules.map((rule, index) => mapToViewModel(rule, index));

    if (!searchText) {
      return viewModels;
    }

    return viewModels.filter(
      item =>
        item.userEmail.toLowerCase().includes(searchText.toLowerCase()) ||
        item.ruleName.toLowerCase().includes(searchText.toLowerCase()) ||
        item.ruleInternalName.toLowerCase().includes(searchText.toLowerCase()) ||
        item.permLevelName.toLowerCase().includes(searchText.toLowerCase()) ||
        item.displayUsername.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const getCurrentPageData = () => {
    const data = getFilteredData();
    return data.slice(skip, skip + take);
  };

  const pageChange = (event: GridPageChangeEvent) => {
    setSkip(event.page.skip);
    setTake(event.page.take);
  };

  const handleSearchChange = (e: InputChangeEvent) => {
    setSearchText(e.value);
    setSkip(0);
  };

  if (isLoading) {
    return <>{translate('custom.settings.adminCenter.permissionManagement.loading')}</>;
  }

  if (isError) {
    return <>{translate('custom.settings.adminCenter.permissionManagement.error')}</>;
  }

  const filteredData = getFilteredData();

  return (
    <div className="permissionsManagementContainer">
      <div className="pageHeader">
        <h1 className="pageTitle">{translate('custom.settings.adminCenter.permissionManagement.title')}</h1>
        <p>{translate('custom.settings.adminCenter.permissionManagement.description')}</p>
      </div>
      <RoleExplanationTable rows={permissionRoleExplanations ?? []} />
      <div className="searchFilterContainer">
        <div className="searchContainer">
          <span></span>
          <Input
            placeholder={translate('custom.settings.adminCenter.permissionManagement.searchPlaceholder')}
            value={searchText}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      <Grid
        data={getCurrentPageData()}
        pageable={{
          buttonCount: 5,
          info: true,
          type: 'numeric',
          pageSizes: true,
          previousNext: true
        }}
        skip={skip}
        take={take}
        total={filteredData.length}
        onPageChange={pageChange}
        sortable={true}
        resizable={true}
        style={{ width: '100%' }}
      >
        <GridColumn field="userEmail" title="User" />
        <GridColumn field="ruleName" title="Rule Name" />
        <GridColumn field="permLevelName" title="Permission Level" />
      </Grid>
    </div>
  );
};
